// Destino al que volver después del login. Si la sesión venció y el usuario
// abre la push o el mail de una notificación, ProtectedRoute (o el
// interceptor, vía 'auth:logout') lo manda a /login con `state.from`, y
// LoginPage lo guarda acá: el state del router no sobrevive la ida y vuelta
// a Google. localStorage y no sessionStorage porque en la PWA de Android la
// vuelta de Google puede caer en otro contexto de navegación; por eso vence
// solo.

const STORAGE_KEY = "postLoginRedirect";
const TTL_MS = 30 * 60 * 1000;

// Rutas a las que no tiene sentido volver después de entrar.
const AUTH_PATHS = [
  "/login",
  "/register",
  "/oauth",
  "/setup-password",
  "/change-password",
];

/**
 * Ruta interna segura: empieza con "/" y no con "//" ni "/\", que el
 * navegador interpreta como otro sitio. Tampoco caracteres de control: el
 * parser de URL descarta tabs y saltos, así que "/\t/evil.com" termina siendo
 * "//evil.com".
 */
export const isSafeInternalPath = (path: unknown): path is string =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("\\") &&
  !/[\u0000-\u001f\u007f]/.test(path);

const isWorthReturning = (path: string): boolean =>
  path !== "/" &&
  !AUTH_PATHS.some((auth) => path === auth || path.startsWith(`${auth}/`) || path.startsWith(`${auth}?`));

export const savePostLoginRedirect = (path: unknown): void => {
  try {
    if (!isSafeInternalPath(path) || !isWorthReturning(path)) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ path, at: Date.now() }),
    );
  } catch {
    // Storage bloqueado: después del login se va al inicio, como antes.
  }
};

export const clearPostLoginRedirect = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
};

/** Devuelve el destino guardado (una sola vez) o null si no hay, venció o no es seguro. */
export const consumePostLoginRedirect = (): string | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof at !== "number" || Date.now() - at > TTL_MS) return null;
    return isSafeInternalPath(path) && isWorthReturning(path) ? path : null;
  } catch {
    return null;
  }
};

/** Ruta actual completa (con query y ancla), para pasarla como `state.from`. */
export const currentPath = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;
